import { fg } from '@atlaskit/platform-feature-flags/fg';

import { type EditionAwarenessContextType } from './edition-awareness-context';
import { isEditionOneOf } from './is-edition-one-of';

/**
 * How far below the allowance, in GB, the near-limit band starts (TAIYO-553). Sites in
 * `[limit - 1, limit)` see the "Near limit" pill and modal; at or over the limit they see
 * "Storage full".
 */
export const JIRA_STANDARD_STORAGE_NEAR_LIMIT_GB_THRESHOLD = 1;

/**
 * Jira Software Standard sites that are within `JIRA_STANDARD_STORAGE_NEAR_LIMIT_GB_THRESHOLD` of, at, or
 * over their attachment storage allowance (JSSHL, TAIYO-388 and TAIYO-553).
 *
 * Sibling of `isEligibleForStorageHardLimit`, which is Free-only and fully rolled out. This one
 * deliberately reads no entitlement-derived field (`canUpgrade`, `canDirectBuyUpgrade`,
 * `BILLING_ADMIN`, `isInCollection`): a paid, non-trialling Standard tenant resolves a null
 * entitlement under the root query's filter, so those are never available here. Collection
 * membership and the Premium change-offering capability are resolved separately by
 * `JiraStandardStorageHardLimit` in the Standard dispatch, which excludes collection sites and
 * gates the near-limit band on `canUpgradeToPremium`.
 *
 * Does not fire exposure. The experiment cohort is read where the dialog is dispatched.
 */
export function isEligibleForJiraStandardStorageHardLimit(
	context: EditionAwarenessContextType,
): boolean {
	const {
		product,
		edition,
		isInTrial,
		isInPredunning,
		storageUsageInGB,
		storageLimitInGB,
		storageIsUnlimited,
	} = context;

	if (!storageUsageInGB || !storageLimitInGB) {
		return false;
	}

	return Boolean(
		product === 'jira' &&
		isEditionOneOf(edition, ['standard']) &&
		!isInTrial &&
		!isInPredunning &&
		!storageIsUnlimited &&
		// Includes the near-limit band from `limit - 1 GB`. Who sees that band is not decided here:
		// the dispatch and the pill gate it on `canUpgradeToPremium`.
		storageUsageInGB >= storageLimitInGB - JIRA_STANDARD_STORAGE_NEAR_LIMIT_GB_THRESHOLD &&
		fg('jira-standard-storage-hard-limits-kill-switch'),
	);
}

/**
 * Whether a site is in the near-limit band: within `JIRA_STANDARD_STORAGE_NEAR_LIMIT_GB_THRESHOLD` of its
 * allowance but not yet at it. Reads no gate and no experiment; callers combine it with
 * `isEligibleForJiraStandardStorageHardLimit` to pick between the near-limit and hard-limit
 * variants.
 */
export function isJiraStandardStorageNearLimit(context: EditionAwarenessContextType): boolean {
	const { storageUsageInGB, storageLimitInGB } = context;

	if (!storageUsageInGB || !storageLimitInGB) {
		return false;
	}

	return (
		storageUsageInGB >= storageLimitInGB - JIRA_STANDARD_STORAGE_NEAR_LIMIT_GB_THRESHOLD &&
		storageUsageInGB < storageLimitInGB
	);
}
